import { CSElementPlugin } from '../types/plugin-interfaces';
import { CSElement } from '../core/CSElement';

// Дополнительные типы для плагина
export interface PluginConfig {
    enabled?: boolean;
    [key: string]: any;
}

export interface PluginContext {
    element?: CSElement;
    operation?: string;
    args?: any[];
    metadata?: Record<string, any>;
    timestamp?: Date;
    userId?: string;
    sessionId?: string;
}

export interface PluginResult {
    success: boolean;
    data?: any;
    error?: string;
    metadata?: Record<string, any>;
}

// Типы аналитики
export enum EventType {
    CREATE = 'create',
    READ = 'read',
    UPDATE = 'update',
    DELETE = 'delete',
    QUERY = 'query',
    SERIALIZE = 'serialize',
    CUSTOM = 'custom'
}

export enum MetricType {
    COUNTER = 'counter',
    GAUGE = 'gauge',
    HISTOGRAM = 'histogram',
    TIMER = 'timer',
    RATE = 'rate'
}

export enum AggregationType {
    SUM = 'sum',
    AVERAGE = 'average',
    MIN = 'min',
    MAX = 'max',
    COUNT = 'count',
    PERCENTILE = 'percentile'
}

export enum ReportFormat {
    JSON = 'json',
    CSV = 'csv',
    HTML = 'html',
    PDF = 'pdf',
    EXCEL = 'excel'
}

// Интерфейсы аналитики
export interface AnalyticsEvent {
    id: string;
    type: EventType;
    elementId?: string;
    userId?: string;
    sessionId?: string;
    timestamp: Date;
    duration?: number;
    success: boolean;
    error?: string;
    metadata: Record<string, any>;
    tags: string[];
    category?: string;
    source?: string;
    userAgent?: string;
    ipAddress?: string;
}

export interface Metric {
    id: string;
    name: string;
    type: MetricType;
    value: number;
    timestamp: Date;
    tags: Record<string, string>;
    description?: string;
    unit?: string;
}

export interface Dimension {
    name: string;
    value: string;
    type: 'string' | 'number' | 'boolean' | 'date';
}

export interface AnalyticsQuery {
    eventTypes?: EventType[];
    dateRange?: {
        start: Date;
        end: Date;
    };
    filters?: Record<string, any>;
    groupBy?: string[];
    aggregation?: AggregationType;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface AnalyticsReport {
    id: string;
    name: string;
    description?: string;
    query: AnalyticsQuery;
    format: ReportFormat;
    createdAt: Date;
    updatedAt: Date;
    data: any[];
    summary: Record<string, any>;
    metadata: Record<string, any>;
}

export interface Dashboard {
    id: string;
    name: string;
    description?: string;
    widgets: Widget[];
    layout: DashboardLayout;
    filters: Record<string, any>;
    refreshInterval?: number;
    isPublic: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface Widget {
    id: string;
    type: 'chart' | 'table' | 'metric' | 'text';
    title: string;
    query: AnalyticsQuery;
    config: Record<string, any>;
    position: { x: number; y: number; width: number; height: number };
}

export interface DashboardLayout {
    columns: number;
    rows: number;
    gap: number;
    responsive: boolean;
}

export interface UserBehavior {
    userId: string;
    sessionId: string;
    startTime: Date;
    endTime?: Date;
    events: AnalyticsEvent[];
    elementsAccessed: string[];
    operationsPerformed: Record<EventType, number>;
    timeSpentByElement: Record<string, number>;
    errorCount: number;
    successRate: number;
}

export interface PerformanceMetrics {
    averageResponseTime: number;
    throughput: number;
    errorRate: number;
    memoryUsage: number;
    cpuUsage: number;
    operationsPerSecond: Record<EventType, number>;
    slowestOperations: Array<{ operation: string; duration: number; timestamp: Date }>;
    peakUsageTime: Date;
}

// Конфигурация плагина
export interface AnalyticsPluginConfig extends PluginConfig {
    enableRealTimeTracking: boolean;
    enableUserBehaviorTracking: boolean;
    enablePerformanceTracking: boolean;
    enableCustomEvents: boolean;
    samplingRate: number; // 0-1, процент событий для записи
    maxEventsInMemory: number;
    retentionPeriod: number; // дни
    batchSize: number;
    flushInterval: number; // миллисекунды
    exportFormats: ReportFormat[];
    anonymizeUserData: boolean;
    excludeEvents: EventType[];
    includeMetadata: boolean;
    enableGeolocation: boolean;
}

// Статистика аналитики
export interface AnalyticsStats {
    totalEvents: number;
    eventsToday: number;
    eventsThisWeek: number;
    eventsThisMonth: number;
    eventsByType: Record<EventType, number>;
    uniqueUsers: number;
    uniqueSessions: number;
    averageSessionDuration: number;
    topElements: Array<{ elementId: string; accessCount: number }>;
    topOperations: Array<{ operation: EventType; count: number }>;
    errorRate: number;
    lastFlush?: Date;
    memoryUsage: number;
}

export class AnalyticsPlugin implements CSElementPlugin {
    public readonly name = 'AnalyticsPlugin';
    public readonly version = '1.0.0';
    public readonly description = 'Плагин для сбора аналитики использования и отслеживания событий';

    private config: AnalyticsPluginConfig;
    private events: AnalyticsEvent[] = [];
    private metrics: Metric[] = [];
    private reports = new Map<string, AnalyticsReport>();
    private dashboards = new Map<string, Dashboard>();
    private userBehaviors = new Map<string, UserBehavior>();
    private performanceMetrics: PerformanceMetrics;
    private stats: AnalyticsStats;
    private flushTimer?: NodeJS.Timeout;
    private sessionStartTimes = new Map<string, Date>();

    constructor(config: Partial<AnalyticsPluginConfig> = {}) {
        this.config = {
            enabled: true,
            priority: 50,
            enableRealTimeTracking: true,
            enableUserBehaviorTracking: true,
            enablePerformanceTracking: true,
            enableCustomEvents: true,
            samplingRate: 1.0,
            maxEventsInMemory: 10000,
            retentionPeriod: 30,
            batchSize: 100,
            flushInterval: 60000, // 1 минута
            exportFormats: [ReportFormat.JSON, ReportFormat.CSV],
            anonymizeUserData: false,
            excludeEvents: [],
            includeMetadata: true,
            enableGeolocation: false,
            ...config
        };

        this.stats = {
            totalEvents: 0,
            eventsToday: 0,
            eventsThisWeek: 0,
            eventsThisMonth: 0,
            eventsByType: {} as Record<EventType, number>,
            uniqueUsers: 0,
            uniqueSessions: 0,
            averageSessionDuration: 0,
            topElements: [],
            topOperations: [],
            errorRate: 0,
            memoryUsage: 0
        };

        this.performanceMetrics = {
            averageResponseTime: 0,
            throughput: 0,
            errorRate: 0,
            memoryUsage: 0,
            cpuUsage: 0,
            operationsPerSecond: {} as Record<EventType, number>,
            slowestOperations: [],
            peakUsageTime: new Date()
        };

        // Инициализация счетчиков событий
        Object.values(EventType).forEach(type => {
            this.stats.eventsByType[type] = 0;
            this.performanceMetrics.operationsPerSecond[type] = 0;
        });
    }

    // Реализация интерфейса CSElementPlugin
    install(CSElementClass: typeof CSElement): void {
        // Устанавливаем плагин в CSElement
        // Здесь можно добавить хуки и middleware для отслеживания событий
        console.log(`AnalyticsPlugin установлен для ${CSElementClass.name}`);
    }

    async initialize(): Promise<void> {
        console.log(`Инициализация ${this.name} v${this.version}`);
        
        // Запуск периодической отправки данных
        if (this.config.flushInterval > 0) {
            this.flushTimer = setInterval(() => {
                this.flushEvents();
            }, this.config.flushInterval);
        }

        // Очистка старых данных
        setInterval(() => {
            this.cleanupOldData();
        }, 24 * 60 * 60 * 1000); // каждый день

        // Обновление статистики
        setInterval(() => {
            this.updateStats();
        }, 60000); // каждую минуту
    }

    async destroy(): Promise<void> {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }
        
        // Финальная отправка данных
        await this.flushEvents();
        
        console.log(`${this.name} деинициализирован`);
    }

    getConfig(): AnalyticsPluginConfig {
        return { ...this.config };
    }

    updateConfig(newConfig: Partial<AnalyticsPluginConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    // Lifecycle hooks
    async beforeCreate(element: CSElement, context: PluginContext): Promise<PluginResult> {
        const startTime = Date.now();
        
        return this.withPerformanceTracking(async () => {
            await this.trackEvent({
                type: EventType.CREATE,
                elementId: element.id,
                success: true,
                metadata: {
                    elementType: element.constructor.name,
                    hasChildren: element.elementsCount() > 0,
                    dataKeys: Array.from(element.data.keys())
                }
            }, context);

            return { success: true };
        }, EventType.CREATE, startTime);
    }

    async afterCreate(element: CSElement, context: PluginContext): Promise<PluginResult> {
        // Обновление пользовательского поведения
        if (this.config.enableUserBehaviorTracking) {
            this.updateUserBehavior(context, EventType.CREATE, element.id);
        }

        return { success: true };
    }

    async beforeRead(element: CSElement, context: PluginContext): Promise<PluginResult> {
        const startTime = Date.now();
        
        return this.withPerformanceTracking(async () => {
            await this.trackEvent({
                type: EventType.READ,
                elementId: element.id,
                success: true,
                metadata: {
                    dataSize: JSON.stringify(Object.fromEntries(element.data)).length,
                    childrenCount: element.elementsCount()
                }
            }, context);

            return { success: true };
        }, EventType.READ, startTime);
    }

    async beforeUpdate(element: CSElement, context: PluginContext): Promise<PluginResult> {
        const startTime = Date.now();
        
        return this.withPerformanceTracking(async () => {
            await this.trackEvent({
                type: EventType.UPDATE,
                elementId: element.id,
                success: true,
                metadata: {
                    previousDataSize: JSON.stringify(Object.fromEntries(element.data)).length,
                    operation: context.operation
                }
            }, context);

            return { success: true };
        }, EventType.UPDATE, startTime);
    }

    async beforeDelete(element: CSElement, context: PluginContext): Promise<PluginResult> {
        const startTime = Date.now();
        
        return this.withPerformanceTracking(async () => {
            await this.trackEvent({
                type: EventType.DELETE,
                elementId: element.id,
                success: true,
                metadata: {
                    hadChildren: element.elementsCount() > 0,
                    dataKeys: Array.from(element.data.keys())
                }
            }, context);

            return { success: true };
        }, EventType.DELETE, startTime);
    }

    async onError(error: Error, context: PluginContext): Promise<PluginResult> {
        await this.trackEvent({
            type: EventType.CUSTOM,
            success: false,
            error: error.message,
            metadata: {
                errorType: error.constructor.name,
                stack: error.stack,
                operation: context.operation
            },
            category: 'error'
        }, context);

        return { success: true };
    }

    // Методы отслеживания
    async trackEvent(eventData: Partial<AnalyticsEvent>, context: PluginContext): Promise<void> {
        if (!this.config.enabled || !this.shouldSampleEvent()) {
            return;
        }

        if (eventData.type && this.config.excludeEvents.includes(eventData.type)) {
            return;
        }

        const event: AnalyticsEvent = {
            id: this.generateId(),
            type: eventData.type || EventType.CUSTOM,
            timestamp: new Date(),
            tags: [],
            metadata: {},
            success: true,
            ...eventData,
            userId: this.config.anonymizeUserData ? this.hashUserId(context.userId) : context.userId,
            sessionId: context.sessionId,
            userAgent: context.metadata?.userAgent as string,
            ipAddress: this.config.anonymizeUserData ? this.hashIpAddress(context.metadata?.ipAddress as string) : context.metadata?.ipAddress as string
        };

        this.events.push(event);
        this.stats.totalEvents++;
        this.stats.eventsByType[event.type]++;

        // Ограничение памяти
        if (this.events.length > this.config.maxEventsInMemory) {
            await this.flushEvents();
        }

        // Обновление метрик в реальном времени
        if (this.config.enableRealTimeTracking) {
            this.updateRealTimeMetrics(event);
        }
    }

    async trackCustomEvent(name: string, data: Record<string, any>, context: PluginContext): Promise<void> {
        if (!this.config.enableCustomEvents) return;

        await this.trackEvent({
            type: EventType.CUSTOM,
            success: true,
            metadata: { customEventName: name, ...data },
            category: 'custom',
            tags: [name]
        }, context);
    }

    recordMetric(name: string, value: number, type: MetricType = MetricType.GAUGE, tags: Record<string, string> = {}): void {
        const metric: Metric = {
            id: this.generateId(),
            name,
            type,
            value,
            timestamp: new Date(),
            tags
        };

        this.metrics.push(metric);
    }

    // Запросы и отчеты
    async query(query: AnalyticsQuery): Promise<AnalyticsEvent[]> {
        let filteredEvents = [...this.events];

        // Фильтрация по типам событий
        if (query.eventTypes && query.eventTypes.length > 0) {
            filteredEvents = filteredEvents.filter(event => 
                query.eventTypes!.includes(event.type)
            );
        }

        // Фильтрация по дате
        if (query.dateRange) {
            filteredEvents = filteredEvents.filter(event => 
                event.timestamp >= query.dateRange!.start && 
                event.timestamp <= query.dateRange!.end
            );
        }

        // Дополнительные фильтры
        if (query.filters) {
            filteredEvents = filteredEvents.filter(event => {
                return Object.entries(query.filters!).every(([key, value]) => {
                    const eventValue = this.getNestedValue(event, key);
                    return eventValue === value;
                });
            });
        }

        // Сортировка
        if (query.sortBy) {
            filteredEvents.sort((a, b) => {
                const aValue = this.getNestedValue(a, query.sortBy!);
                const bValue = this.getNestedValue(b, query.sortBy!);
                
                if (query.sortOrder === 'desc') {
                    return bValue > aValue ? 1 : -1;
                }
                return aValue > bValue ? 1 : -1;
            });
        }

        // Пагинация
        if (query.offset || query.limit) {
            const start = query.offset || 0;
            const end = query.limit ? start + query.limit : undefined;
            filteredEvents = filteredEvents.slice(start, end);
        }

        return filteredEvents;
    }

    async generateReport(name: string, query: AnalyticsQuery, format: ReportFormat = ReportFormat.JSON): Promise<AnalyticsReport> {
        const events = await this.query(query);
        const summary = this.calculateSummary(events);

        const report: AnalyticsReport = {
            id: this.generateId(),
            name,
            query,
            format,
            createdAt: new Date(),
            updatedAt: new Date(),
            data: events,
            summary,
            metadata: {
                totalEvents: events.length,
                generatedAt: new Date(),
                format
            }
        };

        this.reports.set(report.id, report);
        return report;
    }

    async exportReport(reportId: string, format?: ReportFormat): Promise<string> {
        const report = this.reports.get(reportId);
        if (!report) {
            throw new Error(`Отчет с ID ${reportId} не найден`);
        }

        const exportFormat = format || report.format;

        switch (exportFormat) {
            case ReportFormat.JSON:
                return JSON.stringify(report, null, 2);
            
            case ReportFormat.CSV:
                return this.convertToCSV(report.data);
            
            case ReportFormat.HTML:
                return this.convertToHTML(report);
            
            default:
                throw new Error(`Формат ${exportFormat} не поддерживается`);
        }
    }

    // Дашборды
    createDashboard(dashboardData: Omit<Dashboard, 'id' | 'createdAt' | 'updatedAt'>): Dashboard {
        const dashboard: Dashboard = {
            id: this.generateId(),
            createdAt: new Date(),
            updatedAt: new Date(),
            ...dashboardData
        };

        this.dashboards.set(dashboard.id, dashboard);
        return dashboard;
    }

    getDashboard(id: string): Dashboard | undefined {
        return this.dashboards.get(id);
    }

    updateDashboard(id: string, updates: Partial<Dashboard>): boolean {
        const dashboard = this.dashboards.get(id);
        if (!dashboard) return false;

        Object.assign(dashboard, { ...updates, updatedAt: new Date() });
        return true;
    }

    deleteDashboard(id: string): boolean {
        return this.dashboards.delete(id);
    }

    // Анализ поведения пользователей
    getUserBehavior(userId: string): UserBehavior | undefined {
        return this.userBehaviors.get(userId);
    }

    async analyzeUserBehavior(userId: string): Promise<Record<string, any>> {
        const behavior = this.userBehaviors.get(userId);
        if (!behavior) return {};

        const userEvents = this.events.filter(e => e.userId === userId);
        
        return {
            totalEvents: userEvents.length,
            sessionDuration: behavior.endTime ? 
                behavior.endTime.getTime() - behavior.startTime.getTime() : 
                Date.now() - behavior.startTime.getTime(),
            mostAccessedElements: this.getMostAccessedElements(userEvents),
            operationDistribution: behavior.operationsPerformed,
            averageTimePerElement: this.calculateAverageTimePerElement(behavior),
            errorRate: behavior.errorCount / userEvents.length,
            successRate: behavior.successRate,
            peakActivityTime: this.findPeakActivityTime(userEvents)
        };
    }

    // Метрики производительности
    getPerformanceMetrics(): PerformanceMetrics {
        return { ...this.performanceMetrics };
    }

    async generatePerformanceReport(): Promise<Record<string, any>> {
        const recentEvents = this.events.filter(e => 
            Date.now() - e.timestamp.getTime() < 24 * 60 * 60 * 1000 // последние 24 часа
        );

        return {
            totalEvents: recentEvents.length,
            averageResponseTime: this.performanceMetrics.averageResponseTime,
            throughput: this.performanceMetrics.throughput,
            errorRate: this.performanceMetrics.errorRate,
            operationsPerSecond: this.performanceMetrics.operationsPerSecond,
            slowestOperations: this.performanceMetrics.slowestOperations.slice(0, 10),
            memoryUsage: this.performanceMetrics.memoryUsage,
            peakUsageTime: this.performanceMetrics.peakUsageTime,
            eventDistribution: this.calculateEventDistribution(recentEvents)
        };
    }

    // Утилиты
    private async withPerformanceTracking<T>(
        operation: () => Promise<T>, 
        eventType: EventType, 
        startTime: number
    ): Promise<T> {
        try {
            const result = await operation();
            const duration = Date.now() - startTime;
            
            if (this.config.enablePerformanceTracking) {
                this.recordPerformanceMetric(eventType, duration, true);
            }
            
            return result;
        } catch (error) {
            const duration = Date.now() - startTime;
            
            if (this.config.enablePerformanceTracking) {
                this.recordPerformanceMetric(eventType, duration, false);
            }
            
            throw error;
        }
    }

    private recordPerformanceMetric(eventType: EventType, duration: number, success: boolean): void {
        // Обновление средних значений
        const currentAvg = this.performanceMetrics.averageResponseTime;
        this.performanceMetrics.averageResponseTime = (currentAvg + duration) / 2;

        // Обновление throughput
        this.performanceMetrics.throughput++;

        // Обновление error rate
        if (!success) {
            this.performanceMetrics.errorRate = 
                (this.performanceMetrics.errorRate + 1) / this.performanceMetrics.throughput;
        }

        // Запись медленных операций
        if (duration > 1000) { // операции дольше 1 секунды
            this.performanceMetrics.slowestOperations.push({
                operation: eventType,
                duration,
                timestamp: new Date()
            });

            // Ограничение размера списка
            if (this.performanceMetrics.slowestOperations.length > 100) {
                this.performanceMetrics.slowestOperations = 
                    this.performanceMetrics.slowestOperations.slice(-100);
            }
        }
    }

    private updateUserBehavior(context: PluginContext, eventType: EventType, elementId?: string): void {
        const userId = context.userId;
        const sessionId = context.sessionId;
        
        if (!userId || !sessionId) return;

        let behavior = this.userBehaviors.get(userId);
        
        if (!behavior) {
            behavior = {
                userId,
                sessionId,
                startTime: new Date(),
                events: [],
                elementsAccessed: [],
                operationsPerformed: {} as Record<EventType, number>,
                timeSpentByElement: {},
                errorCount: 0,
                successRate: 1
            };
            
            this.userBehaviors.set(userId, behavior);
            this.sessionStartTimes.set(sessionId, new Date());
        }

        // Обновление статистики операций
        behavior.operationsPerformed[eventType] = (behavior.operationsPerformed[eventType] || 0) + 1;
        
        // Добавление элемента в список доступа
        if (elementId && !behavior.elementsAccessed.includes(elementId)) {
            behavior.elementsAccessed.push(elementId);
        }

        // Обновление времени окончания сессии
        behavior.endTime = new Date();
    }

    private shouldSampleEvent(): boolean {
        return Math.random() < this.config.samplingRate;
    }

    private updateRealTimeMetrics(event: AnalyticsEvent): void {
        // Обновление операций в секунду
        if (!this.performanceMetrics.operationsPerSecond[event.type]) {
            this.performanceMetrics.operationsPerSecond[event.type] = 0;
        }
        
        this.performanceMetrics.operationsPerSecond[event.type]++;
    }

    private async flushEvents(): Promise<void> {
        if (this.events.length === 0) return;

        // Здесь должна быть отправка данных во внешнее хранилище
        console.log(`Отправка ${this.events.length} событий в аналитическое хранилище`);
        
        // Очистка буфера
        this.events = [];
        this.stats.lastFlush = new Date();
    }

    private cleanupOldData(): void {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - this.config.retentionPeriod);

        // Очистка старых событий
        const initialEventCount = this.events.length;
        this.events = this.events.filter(event => event.timestamp > cutoffDate);
        
        // Очистка старых метрик
        this.metrics = this.metrics.filter(metric => metric.timestamp > cutoffDate);

        console.log(`Очищено ${initialEventCount - this.events.length} старых событий`);
    }

    private updateStats(): void {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        this.stats.eventsToday = this.events.filter(e => e.timestamp >= today).length;
        this.stats.eventsThisWeek = this.events.filter(e => e.timestamp >= thisWeek).length;
        this.stats.eventsThisMonth = this.events.filter(e => e.timestamp >= thisMonth).length;

        // Уникальные пользователи и сессии
        const uniqueUsers = new Set(this.events.map(e => e.userId).filter(Boolean));
        const uniqueSessions = new Set(this.events.map(e => e.sessionId).filter(Boolean));
        
        this.stats.uniqueUsers = uniqueUsers.size;
        this.stats.uniqueSessions = uniqueSessions.size;

        // Топ элементы и операции
        this.stats.topElements = this.calculateTopElements();
        this.stats.topOperations = this.calculateTopOperations();

        // Error rate
        const totalEvents = this.events.length;
        const errorEvents = this.events.filter(e => !e.success).length;
        this.stats.errorRate = totalEvents > 0 ? errorEvents / totalEvents : 0;

        // Использование памяти
        this.stats.memoryUsage = this.estimateMemoryUsage();
    }

    private calculateSummary(events: AnalyticsEvent[]): Record<string, any> {
        return {
            totalEvents: events.length,
            uniqueUsers: new Set(events.map(e => e.userId).filter(Boolean)).size,
            uniqueSessions: new Set(events.map(e => e.sessionId).filter(Boolean)).size,
            eventTypes: this.groupBy(events, 'type'),
            successRate: events.filter(e => e.success).length / events.length,
            averageDuration: events.filter(e => e.duration).reduce((sum, e) => sum + (e.duration || 0), 0) / events.filter(e => e.duration).length,
            timeRange: {
                start: Math.min(...events.map(e => e.timestamp.getTime())),
                end: Math.max(...events.map(e => e.timestamp.getTime()))
            }
        };
    }

    private calculateTopElements(): Array<{ elementId: string; accessCount: number }> {
        const elementCounts = new Map<string, number>();
        
        this.events.forEach(event => {
            if (event.elementId) {
                elementCounts.set(event.elementId, (elementCounts.get(event.elementId) || 0) + 1);
            }
        });

        return Array.from(elementCounts.entries())
            .map(([elementId, accessCount]) => ({ elementId, accessCount }))
            .sort((a, b) => b.accessCount - a.accessCount)
            .slice(0, 10);
    }

    private calculateTopOperations(): Array<{ operation: EventType; count: number }> {
        return Object.entries(this.stats.eventsByType)
            .map(([operation, count]) => ({ operation: operation as EventType, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }

    private getMostAccessedElements(events: AnalyticsEvent[]): Array<{ elementId: string; count: number }> {
        const counts = new Map<string, number>();
        
        events.forEach(event => {
            if (event.elementId) {
                counts.set(event.elementId, (counts.get(event.elementId) || 0) + 1);
            }
        });

        return Array.from(counts.entries())
            .map(([elementId, count]) => ({ elementId, count }))
            .sort((a, b) => b.count - a.count);
    }

    private calculateAverageTimePerElement(behavior: UserBehavior): Record<string, number> {
        const result: Record<string, number> = {};
        
        Object.entries(behavior.timeSpentByElement).forEach(([elementId, totalTime]) => {
            const accessCount = behavior.elementsAccessed.filter(id => id === elementId).length;
            result[elementId] = totalTime / accessCount;
        });

        return result;
    }

    private findPeakActivityTime(events: AnalyticsEvent[]): Date | null {
        if (events.length === 0) return null;

        const hourCounts = new Map<number, number>();
        
        events.forEach(event => {
            const hour = event.timestamp.getHours();
            hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
        });

        const peakHour = Array.from(hourCounts.entries())
            .sort((a, b) => b[1] - a[1])[0];

        if (!peakHour) return null;

        const peakDate = new Date();
        peakDate.setHours(peakHour[0], 0, 0, 0);
        return peakDate;
    }

    private calculateEventDistribution(events: AnalyticsEvent[]): Record<string, number> {
        const distribution: Record<string, number> = {};
        
        events.forEach(event => {
            const hour = event.timestamp.getHours();
            const key = `${hour}:00`;
            distribution[key] = (distribution[key] || 0) + 1;
        });

        return distribution;
    }

    private convertToCSV(data: any[]): string {
        if (data.length === 0) return '';

        const headers = Object.keys(data[0]);
        const csvRows = [headers.join(',')];

        data.forEach(row => {
            const values = headers.map(header => {
                const value = row[header];
                return typeof value === 'string' ? `"${value}"` : value;
            });
            csvRows.push(values.join(','));
        });

        return csvRows.join('\n');
    }

    private convertToHTML(report: AnalyticsReport): string {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Отчет: ${report.name}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; }
                    .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; }
                </style>
            </head>
            <body>
                <h1>Отчет: ${report.name}</h1>
                <div class="summary">
                    <h2>Сводка</h2>
                    <pre>${JSON.stringify(report.summary, null, 2)}</pre>
                </div>
                <h2>Данные</h2>
                <p>Сгенерировано: ${report.createdAt.toLocaleString()}</p>
                <p>Всего событий: ${report.data.length}</p>
            </body>
            </html>
        `;
    }

    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }

    private groupBy(array: any[], key: string): Record<string, number> {
        return array.reduce((groups, item) => {
            const value = this.getNestedValue(item, key);
            groups[value] = (groups[value] || 0) + 1;
            return groups;
        }, {});
    }

    private estimateMemoryUsage(): number {
        // Примерная оценка использования памяти в байтах
        const eventSize = JSON.stringify(this.events[0] || {}).length;
        const metricSize = JSON.stringify(this.metrics[0] || {}).length;
        
        return (this.events.length * eventSize) + (this.metrics.length * metricSize);
    }

    private hashUserId(userId?: string): string | undefined {
        if (!userId) return undefined;
        // Простое хэширование для анонимизации
        return 'user_' + userId.split('').reduce((hash, char) => 
            ((hash << 5) - hash) + char.charCodeAt(0), 0
        ).toString(36);
    }

    private hashIpAddress(ip?: string): string | undefined {
        if (!ip) return undefined;
        // Маскирование IP адреса
        const parts = ip.split('.');
        if (parts.length === 4) {
            return `${parts[0]}.${parts[1]}.xxx.xxx`;
        }
        return 'masked_ip';
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // Публичные методы для получения данных
    getStats(): AnalyticsStats {
        return { ...this.stats };
    }

    getAllEvents(): AnalyticsEvent[] {
        return [...this.events];
    }

    getAllMetrics(): Metric[] {
        return [...this.metrics];
    }

    getAllReports(): AnalyticsReport[] {
        return Array.from(this.reports.values());
    }

    getAllDashboards(): Dashboard[] {
        return Array.from(this.dashboards.values());
    }

    getAllUserBehaviors(): UserBehavior[] {
        return Array.from(this.userBehaviors.values());
    }
} 